import { faComment } from "@fortawesome/pro-regular-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import React from "react";

import LikeControl from "@/mk2/apps/blogs/components/LikeControl";
import LikeSection from "@/mk2/apps/blogs/containers/LikeSection/LikeSection";
import { blogsCommentEditUrl } from "@/mk2/apps/blogs/urls";
import ReplyButton from "@/mk2/apps/forum/components/ReplyButton";
import { loginUrl } from "@/mk2/apps/users/urls";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { Dot } from "@/mk2/components/Dot";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import EmojiReaction from "@/mk2/containers/EmojiReaction/EmojiReaction";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { CommentEntity, UserEntity } from "@/mk2/schemas";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import {
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_COMMENT,
    DELETE_COMMENT,
    FORUM_COMMENT_EDIT_MENU,
    REPLY_LINK,
} from "@/mk/autogenerated/translation";
import {
    getAnonymousUserPseudonym,
    isAnonymousUser,
    isAuthenticated,
} from "@/mk/bazaar/common/userUtils";

import styles from "./CommentFooter.mscss";

interface OwnProps {
    className?: string;
    requestUser: UserEntity;
    comment: CommentEntity;
    inForum: boolean;
    inGroups?: boolean;
    isCollapsed: boolean;
    location: HistoryLocationWithState;

    hasDelete: boolean;
    hasEdit: boolean;
    hasId: boolean;
    hasLike: boolean;
    hasLikeDisabled: boolean;
    hasReply: boolean;
    hasReport: boolean;

    onReply(username: string, repliedToCommentId: number);
    onDelete(commentId: number);
    onReport(
        entityType: ComplaintTargetType,
        commentId: number,
        location: HistoryLocationWithState,
    );
}

type Props = OwnProps;

class CommentFooter extends React.PureComponent<Props> {
    public render() {
        const {
            className,
            comment,
            requestUser,
            inForum,
            inGroups,
            isCollapsed,
            location,
            hasDelete,
            hasEdit,
            hasId,
            hasLike,
            hasLikeDisabled,
            hasReply,
            hasReport,
        } = this.props;

        const showDelete = hasDelete && comment.canDelete;
        const showEdit = hasEdit;
        const showId = hasId;
        const showLike = hasLike && !comment.deletedAt;
        const showReply = hasReply && !comment.deletedAt && !comment.byAdmin;
        const showReport = hasReport && !comment.byAdmin;

        const showCreateTime =
            inGroups &&
            (!comment.deletedAt || (comment.deletedAt && !isCollapsed)) &&
            comment.created;

        return inForum || inGroups ? (
            showLike || showReply ? (
                <div
                    className={cx(
                        styles.CommentFooter,
                        inGroups && styles.CommentFooter__inGroups,
                        className,
                    )}
                >
                    {showLike &&
                        (inForum ? (
                            <EmojiReaction
                                entity="forum.message"
                                id={comment.id}
                                disabled={hasLikeDisabled}
                                location={location}
                                noLeftPadding
                            />
                        ) : (
                            <Like
                                id={comment.id}
                                className={cx(inGroups && styles.CommentFooter__inGroups__like)}
                                entity="comment"
                                type={LikeType.IconCount}
                                disabled={hasLikeDisabled}
                                location={location}
                            />
                        ))}

                    {showReply && (
                        <ReplyButton
                            className={cx(inGroups && styles.CommentFooter__inGroups__replyButton)}
                            onClick={
                                isAuthenticated(requestUser) || inForum ? this.handleOnReply : null
                            }
                            link={
                                !isAuthenticated(requestUser) && !inForum
                                    ? url(loginUrl, {}, { next: location.pathname })
                                    : null
                            }
                            asAnchor={!isAuthenticated(requestUser) && !inForum}
                        />
                    )}

                    {showId && (
                        <div className={cx(inGroups && styles.CommentFooter__inGroups__commentId)}>
                            <div>
                                <Dot />
                                {comment.id}
                            </div>
                        </div>
                    )}

                    {showCreateTime && (
                        <div className={styles.CommentFooter__inGroups__createdTime}>
                            {timeAt(comment.created)}
                        </div>
                    )}
                </div>
            ) : null
        ) : (
            <div className={cx(styles.CommentFooter, styles["CommentFooter--inBlogs"], className)}>
                {timeAt(comment.created)}

                {showLike && [
                    <div key="like-control-sep" className={styles.CommentFooter__sep}>
                        •
                    </div>,
                    <LikeControl
                        key="like-control"
                        entity="comment"
                        id={comment.id}
                        requestUser={requestUser}
                        isLikeable={comment.isLikeable}
                        location={location}
                    />,
                ]}
                {showLike &&
                    comment.likeable.likesCount > 0 && [
                        <div key="like-section-sep" className={styles.CommentFooter__sep}>
                            •
                        </div>,
                        <LikeSection
                            key="like-section"
                            entityType="comment"
                            entityId={comment.id}
                            likesCount={comment.likeable.likesCount}
                        />,
                    ]}

                {showReply && [
                    <div key="reply-sep" className={styles.CommentFooter__sep}>
                        •
                    </div>,
                    <Btn
                        key="reply-btn"
                        className={styles.CommentFooter__replyBlogsBtn}
                        type={BtnType.BlueTextLight}
                        icon={inForum && faComment}
                        label={REPLY_LINK}
                        onClick={isAuthenticated(requestUser) ? this.handleOnReply : null}
                        link={
                            !isAuthenticated(requestUser)
                                ? url(loginUrl, {}, { next: location.pathname })
                                : null
                        }
                        sidePadding={BtnSidePadding.None}
                        asAnchor={!isAuthenticated(requestUser)}
                    />,
                ]}

                {showDelete && [
                    <div key="delete-sep" className={styles.CommentFooter__sep}>
                        •
                    </div>,
                    <Btn
                        key="delete-btn"
                        className={styles.CommentFooter__deleteBlogsBtn}
                        onClick={this.handleOnDelete}
                        label={DELETE_COMMENT}
                        sidePadding={BtnSidePadding.None}
                        type={BtnType.BlueTextLight}
                    />,
                ]}

                {showEdit && [
                    <div key="edit-sep" className={styles.CommentFooter__sep}>
                        •
                    </div>,
                    <Btn
                        key="edit-btn"
                        className={styles.CommentFooter__editBlogsBtn}
                        link={url(blogsCommentEditUrl, { commentId: comment.id })}
                        label={FORUM_COMMENT_EDIT_MENU}
                        sidePadding={BtnSidePadding.None}
                        type={BtnType.BlueTextLight}
                    />,
                ]}

                {showReport && [
                    <div key="report-sep" className={styles.CommentFooter__sep}>
                        •
                    </div>,
                    <Btn
                        key="report"
                        className={styles.CommentFooter__reportBlogsBtn}
                        type={BtnType.BlueTextLight}
                        icon={faExclamationTriangle}
                        sidePadding={BtnSidePadding.None}
                        onClick={isAuthenticated(requestUser) ? this.handleOnReport : null}
                        link={
                            !isAuthenticated(requestUser)
                                ? url(loginUrl, {}, { next: location.pathname })
                                : null
                        }
                        asAnchor={!isAuthenticated(requestUser)}
                    />,
                ]}

                {showId && [
                    <div key="id-sep" className={styles.CommentFooter__sep}>
                        •
                    </div>,
                    <div key="id">{comment.id}</div>,
                ]}
            </div>
        );
    }

    private handleOnReply = () => {
        const { onReply, comment } = this.props;
        const username = isAnonymousUser(comment.authorUser)
            ? getAnonymousUserPseudonym(comment.anonymousAuthorHash)
            : comment.authorUser.username;
        const repliedToCommentId = comment?.id;
        onReply(username, repliedToCommentId);
    };

    private handleOnDelete = () => {
        const { comment, onDelete } = this.props;

        const value = window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_COMMENT);
        if (value) {
            return onDelete(comment.id);
        }
    };

    private handleOnReport = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const { comment, onReport, inForum, location } = this.props;
        onReport(
            inForum ? ComplaintTargetType.ForumMessage : ComplaintTargetType.PhotoblogComment,
            comment.id,
            location,
        );
    };
}

export default CommentFooter;
